<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading LC details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!lcData || Object.keys(lcData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No LC data found</p>
        <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area">
          <!-- Header Section -->
          <div class="header">
            <img
              v-if="getCurrentCompany?.logo_thumb"
              :src="`${getFileServerUrl.replace(/\/$/, '')}/${getCurrentCompany.logo_thumb}`"
              alt="Company Logo"
              class="company-logo"
            />
            <div v-else class="logo-placeholder">
              <v-icon icon="mdi-domain" size="60" color="grey-darken-2"></v-icon>
            </div>
            <div class="company-info">
              <strong>{{ getCurrentCompany?.name || 'Company Name' }}</strong><br>
              {{ getCurrentCompany?.address || 'Company Address' }}<br>
              <span v-if="getCurrentCompany?.email">Email: {{ getCurrentCompany.email }}</span>
              <span v-if="getCurrentCompany?.mobile"> | Phone: {{ getCurrentCompany.mobile }}</span>
            </div>
          </div>

          <!-- LC Title -->
          <div class="lc-header-info">
            <h2 class="lc-title">LETTER OF CREDIT (LC) INVOICE</h2>
            <p class="lc-meta">
              <strong>LC No:</strong> {{ lcData.lc_no || 'N/A' }} | 
              <strong>Open Date:</strong> {{ formatDateView(lcData.lc_open_date) }}
            </p>
          </div>

          <!-- LC Information Section -->
          <div class="section-title">LC Information</div>
          <div class="info-section">
            <table class="info-table">
              <tbody>
                <tr>
                  <td class="info-label"><strong>Supplier:</strong></td>
                  <td class="info-value">{{ lcData.supplier?.supplier_name || 'N/A' }}</td>
                  <td class="info-label"><strong>Shipping Method:</strong></td>
                  <td class="info-value">{{ lcData.shipping_method?.name || lcData.shippingMethod?.name || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Issuing Bank:</strong></td>
                  <td class="info-value">{{ lcData.issuing_bank?.name || lcData.issuing_bank?.code || 'N/A' }}</td>
                  <td class="info-label"><strong>Advising Bank:</strong></td>
                  <td class="info-value">{{ lcData.advising_bank?.name || lcData.advising_bank?.code || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>LC Type:</strong></td>
                  <td class="info-value">{{ getLcTypeName(lcData) }}</td>
                  <td class="info-label"><strong>Expiry Date:</strong></td>
                  <td class="info-value">{{ formatDateView(lcData.expiry_date) }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Status:</strong></td>
                  <td class="info-value">{{ lcData.lc_status || lcData.status || 'Open' }}</td>
                  <td class="info-label"><strong>Currency:</strong></td>
                  <td class="info-value">{{ lcData.currency?.code || lcData.currency?.name || 'USD' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>LC Amount:</strong></td>
                  <td class="info-value">{{ formatCurrency(lcData.lc_amount) }}</td>
                  <td class="info-label"><strong>Margin:</strong></td>
                  <td class="info-value">{{ lcData.margin_percent || 0 }}% ({{ formatCurrency(lcData.margin_amount || 0) }})</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Bank Charge:</strong></td>
                  <td class="info-value">{{ formatCurrency(lcData.bank_charge) }}</td>
                  <td class="info-label"><strong>Swift Charge:</strong></td>
                  <td class="info-value">{{ formatCurrency(lcData.swift_charge) }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Insurance:</strong></td>
                  <td class="info-value">{{ formatCurrency(lcData.insurance) }}</td>
                  <td class="info-label"><strong>Presentation Period:</strong></td>
                  <td class="info-value">{{ lcData.presentation_period || 21 }} Days</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Incoterm:</strong></td>
                  <td class="info-value">{{ lcData.incoterm?.name || 'N/A' }}</td>
                  <td class="info-label"><strong>Payment Term:</strong></td>
                  <td class="info-value">{{ lcData.payment_term?.name || lcData.paymentTerm?.name || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Loading Port:</strong></td>
                  <td class="info-value">{{ lcData.loading_port?.name || lcData.loadingPort?.name || 'N/A' }}</td>
                  <td class="info-label"><strong>Discharge Port:</strong></td>
                  <td class="info-value">{{ lcData.discharge_port?.name || lcData.dischargePort?.name || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Proforma Invoice Details -->
          <div v-if="lcData.pis && lcData.pis.length > 0">
            <div 
              v-for="(pi, piIndex) in lcData.pis" 
              :key="pi.id || piIndex"
              class="pi-section"
            >
              <!-- PI Header Bar -->
              <div class="pi-header-bar">
                <div class="pi-header-content">
                  <span class="pi-header-label"><strong>PI No:</strong> {{ pi.pi_no || pi.code || 'N/A' }}</span>
                  <span class="pi-header-label"><strong>PI Date:</strong> {{ formatDateView(pi.pi_date) }}</span>
                  <span class="pi-header-label"><strong>Currency:</strong> {{ pi.currency?.code || 'N/A' }}</span>
                </div>
              </div>
              
              <!-- PI Details Two Column Layout -->
              <div class="pi-details-container">
                <div class="pi-details-column">
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>Incoterm:</strong></span><span class="pi-detail-value">{{ pi.incoterm?.name || 'N/A' }}</span>
                  </div>
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>Loading Port:</strong></span><span class="pi-detail-value">{{ pi.loadingPort?.name || pi.loading_port?.name || 'N/A' }}</span>
                  </div>
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>Shipping Method:</strong></span><span class="pi-detail-value">{{ pi.shippingMethod?.name || pi.shipping_method?.name || 'N/A' }}</span>
                  </div>
                </div>
                <div class="pi-details-column">
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>Payment Term:</strong></span><span class="pi-detail-value">{{ pi.paymentTerm?.name || pi.payment_term?.name || 'N/A' }}</span>
                  </div>
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>Discharge Port:</strong></span><span class="pi-detail-value">{{ pi.dischargePort?.name || pi.discharge_port?.name || 'N/A' }}</span>
                  </div>
                  <div class="pi-detail-row">
                    <span class="pi-detail-label"><strong>PI Validity:</strong></span><span class="pi-detail-value">{{ pi.validity ? formatDateView(pi.validity) : 'N/A' }}</span>
                  </div>
                </div>
              </div>

              <!-- PI Items Table -->
              <div class="pi-items-section">
                <table class="items-table">
                  <thead>
                    <tr>
                      <th style="width: 5%">SL</th>
                      <th style="width: 12%">Product Code</th>
                      <th style="width: 35%">Description</th>
                      <th style="width: 10%">UOM</th>
                      <th style="width: 10%">HS Code</th>
                      <th style="width: 12%" class="text-right">Qty</th>
                      <th style="width: 13%" class="text-right">Rate</th>
                      <th style="width: 13%" class="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, itemIndex) in (pi.details || [])" :key="itemIndex">
                      <td>{{ itemIndex + 1 }}</td>
                      <td>{{ item.product?.code || 'N/A' }}</td>
                      <td>{{ item.product?.name || item.product_name || 'N/A' }}</td>
                      <td>{{ item.product?.unit?.name || item.uom || 'PCS' }}</td>
                      <td class="text-left">{{ item.hs_code }}</td>
                      <td class="text-right">{{ formatNumber(item.qty) }}</td>
                      <td class="text-right">{{ formatCurrency(item.rate, pi.currency?.code) }}</td>
                      <td class="text-right">{{ formatCurrency(item.amount || (item.qty * item.rate), pi.currency?.code) }}</td>
                    </tr>
                    <tr v-if="!pi.details || pi.details.length === 0">
                      <td colspan="7" class="text-center text-grey">No items found</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- PI Summary -->
              <div class="pi-summary">
                <div class="section-title-small">Summary for {{ pi.pi_no || pi.code }}</div>
                <table class="summary-table">
                  <tbody>
                    <tr>
                      <td><strong>Subtotal:</strong></td>
                      <td class="text-right">{{ formatCurrency(pi.sub_total || 0, pi.currency?.code) }}</td>
                    </tr>
                    <tr>
                      <td><strong>Discount:</strong></td>
                      <td class="text-right">{{ formatCurrency(pi.discount || 0, pi.currency?.code) }}</td>
                    </tr>
                    <tr>
                      <td><strong>Net Total:</strong></td>
                      <td class="text-right">{{ formatCurrency(pi.net_total || 0, pi.currency?.code) }}</td>
                    </tr>
                    <tr>
                      <td><strong>PI Amount (from LC):</strong></td>
                      <td class="text-right"><strong>{{ formatCurrency(pi.pivot?.pi_amount || pi.total_amount || 0, pi.currency?.code) }}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>Total Amount:</strong></td>
                      <td class="text-right"><strong>{{ formatCurrency(pi.total_amount || 0, pi.currency?.code) }}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else-if="lcData && (!lcData.pis || lcData.pis.length === 0)" class="pi-section">
            <div class="section-title">Proforma Invoice Details</div>
            <p class="text-center text-grey pa-4">No Proforma Invoices associated with this LC</p>
          </div>

          <!-- LC Summary Section -->
          <div class="lc-summary-section">
            <div class="section-title">LC Summary</div>
            <table class="summary-table">
              <tbody>
                <tr>
                  <td><strong>Total of All PI:</strong></td>
                  <td class="text-right">{{ formatCurrency(totalPiAmount) }}</td>
                </tr>
                <tr>
                  <td><strong>LC Amount:</strong></td>
                  <td class="text-right">{{ formatCurrency(lcData.lc_amount || 0) }}</td>
                </tr>
                <tr>
                  <td><strong>Difference:</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(differenceAmount) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Signature Section -->
          <div class="signature-area mt-8">
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Prepared By</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Checked By</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Approved By</p>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Action Toolbar at Bottom (Hidden on Print) -->
    <v-row class="d-print-none mb-4" justify="center" v-if="!loading && lcData">
      <v-col cols="12">
        <div class="text-center">
          <v-btn color="primary" class="mr-2" @click="printPage">
            <v-icon left>mdi-printer</v-icon> Print
          </v-btn>
          <v-btn color="success" @click="downloadPDF">
            <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import html2pdf from "html2pdf.js";
  import { ref, onMounted, computed } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import { toast } from "vue3-toastify";
  import { useStore } from "vuex";
  import { formatDateView, formatDateDash } from "@/plugins/helpers";

export default {
  name: "ViewLcImportLcMaster",
  methods: {
    formatDateView,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = ref(false);
    const lcData = ref({});

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount, currencyCode = null) => {
      if (!amount && amount !== 0) return "0.00";
      const formatted = Number(amount).toFixed(2);
      // Use provided currency or fallback to LC currency
      const currency = currencyCode || lcData.value.currency?.code || lcData.value.currency?.name || 'USD';
      return `${currency} ${formatted}`;
    };

    const getLcTypeName = (data) => {
      const lcType = data.lc_type || data.lcType;
      if (!lcType) return 'N/A';
      return lcType.name || lcType.code || 'N/A';
    };

    const totalPiAmount = computed(() => {
      if (!lcData.value.pis || lcData.value.pis.length === 0) return 0;
      // Use pivot.pi_amount if available (from pivot table), otherwise use total_amount
      return lcData.value.pis.reduce((sum, pi) => {
        const amount = parseFloat(pi.pivot?.pi_amount || pi.total_amount || 0) || 0;
        return sum + amount;
      }, 0);
    });

    const differenceAmount = computed(() => {
      const lcAmount = parseFloat(lcData.value.lc_amount || 0);
      return lcAmount - totalPiAmount.value;
    });

    const loadLcData = async (id) => {
      if (!id) {
        toast.error("No LC ID provided");
        router.push({ path: "/lc/import-lc-record" });
        return;
      }

      loading.value = true;
      try {
        const data = await store.dispatch("getLcImportLcMasterById", id);
        lcData.value = data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC data");
        router.push({ path: "/lc/import-lc-record" });
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push({ path: "/lc/import-lc-record" });
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: [0.5, 0.5],
        filename: `lc-invoice-${lcData.value.lc_no || lcData.value.code}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(element).save();
    };

    const printPage = () => {
      const printContent = document.getElementById("printable-area").innerHTML;
      const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}, left=0, top=0`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>LC Invoice - ${lcData.value.lc_no || 'LC'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            .print-container {
              width: 800px;
              margin: auto;
              padding: 25px;
              position: relative;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .header img {
              width: 120px;
              margin-bottom: 10px;
            }
            .company-info {
              font-size: 14px;
              margin-top: 5px;
            }
            h2 {
              margin: 0 0 10px;
            }
            .lc-title {
              text-align: center;
              margin-bottom: 10px;
              font-size: 20px;
              font-weight: bold;
            }
            .lc-meta {
              text-align: center;
              margin-bottom: 15px;
              font-size: 14px;
            }
            .section-title {
              background: #f2f2f2;
              padding: 8px;
              margin-top: 20px;
              font-weight: bold;
              border-left: 4px solid #555;
            }
            .info-section {
              margin-top: 10px;
            }
            .info-item {
              margin-bottom: 8px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 14px;
            }
            table th, table td {
              border: 1px solid #999;
              padding: 8px;
              text-align: left;
            }
            table th {
              background: #eee;
              font-weight: bold;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            .summary-table {
              width: 300px;
              margin-left: auto;
              margin-top: 15px;
              font-size: 15px;
            }
            .signature-area {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
              gap: 20px;
            }
            .signature-box {
              width: 220px;
              text-align: center;
            }
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 40px;
              padding-top: 5px;
            }
            .pi-header-bar {
              background-color: #607d8b;
              color: white;
              padding: 12px 16px;
              border-radius: 4px 4px 0 0;
              margin-top: 10px;
            }
            .pi-header-content {
              display: flex;
              gap: 24px;
              align-items: center;
              flex-wrap: wrap;
            }
            .pi-header-label {
              font-size: 14px;
              color: white;
            }
            .pi-details-container {
              display: flex !important;
              gap: 20px;
              background-color: white;
              border: 1px solid #ddd;
              border-top: none;
              padding: 16px;
              border-radius: 0 0 4px 4px;
            }
            .pi-details-column {
              flex: 1;
              display: flex !important;
              flex-direction: column;
              gap: 12px;
            }
            .pi-detail-row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 8px 0;
              border-bottom: 1px solid #f0f0f0;
            }
            .pi-detail-row:last-child {
              border-bottom: none;
            }
            .pi-detail-label {
              font-size: 14px;
              color: #555;
              white-space: nowrap;
            }
            .pi-detail-value {
              font-size: 14px;
              color: #333;
            }
            @media print {
              .print-container {
                width: 100%;
                padding: 20px;
              }
              .pi-header-bar {
                background-color: #607d8b !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color: white !important;
              }
              .pi-details-container {
                display: flex !important;
                border: 1px solid #ddd !important;
                page-break-inside: avoid;
              }
              .pi-details-column {
                display: flex !important;
                flex-direction: column !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.moveTo(0, 0);
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 1000);
    };

    onMounted(() => {
      if (route.params.id) {
        loadLcData(route.params.id);
      } else {
        toast.error("No LC ID provided");
        router.push({ path: "/lc/import-lc-record" });
      }
    });

    return {
      loading,
      lcData,
      formatNumber,
      formatCurrency,
      getLcTypeName,
      totalPiAmount,
      differenceAmount,
      goBack,
      downloadPDF,
      printPage,
      getCurrentCompany: computed(() => store.getters.getCurrentCompany),
      getFileServerUrl: computed(() => store.getters.getFileServerUrl),
    };
  },
};
</script>

<style scoped>
.print-container {
  width: 800px;
  margin: auto;
  padding: 25px;
  background: white;
  position: relative;
  min-height: 600px;
}

.header {
  text-align: center;
  border-bottom: 2px solid #333;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.header img {
  width: 120px;
  margin-bottom: 10px;
}

.logo-placeholder {
  margin-bottom: 10px;
}

.company-info {
  font-size: 14px;
  margin-top: 5px;
}

.lc-header-info {
  text-align: center;
  margin-bottom: 20px;
}

.lc-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.lc-meta {
  font-size: 14px;
  color: #333;
}

.section-title {
  background: #f2f2f2;
  padding: 8px;
  margin-top: 20px;
  font-weight: bold;
  border-left: 4px solid #555;
  color: #333;
}

.section-title-small {
  background: #f5f5f5;
  padding: 6px;
  margin-top: 15px;
  font-weight: bold;
  font-size: 14px;
}

.info-section {
  margin-top: 10px;
}

.info-item {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 22px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.info-table td {
  padding: 6px 8px;
  border: 1px solid #ddd;
}

.info-table .info-label {
  width: 25%;
  background-color: #f5f5f5;
  font-weight: normal;
}

.info-table .info-value {
  width: 25%;
}

.pi-section {
  margin-top: 30px;
  page-break-inside: avoid;
}

.pi-header-bar {
  background-color: #607d8b;
  color: white;
  padding: 12px 16px;
  border-radius: 4px 4px 0 0;
  margin-top: 10px;
}

.pi-header-content {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.pi-header-label {
  font-size: 14px;
  color: white;
}

.pi-details-container {
  display: flex;
  gap: 20px;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  padding: 16px;
  border-radius: 0 0 4px 4px;
}

.pi-details-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pi-detail-row {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.pi-detail-row:last-child {
  border-bottom: none;
}

.pi-detail-label {
  font-size: 14px;
  color: #555;
  white-space: nowrap;
}

.pi-detail-label strong {
  margin-right: 0;
}

.pi-detail-value {
  font-size: 14px;
  color: #333;
  margin-left: 4px;
}

.pi-header-info {
  margin-top: 10px;
  margin-bottom: 10px;
}

.pi-items-section {
  margin-top: 15px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.items-table th,
.items-table td {
  border: 1px solid #999;
  padding: 8px;
  text-align: left;
}

.items-table th {
  background: #eee;
  font-weight: bold;
  color: #333;
}

.items-table td {
  color: #555;
}

.items-table td.text-right {
  text-align: right;
}

.pi-summary {
  margin-top: 15px;
}

.summary-table {
  width: 300px;
  margin-left: auto;
  margin-top: 10px;
  font-size: 15px;
  border-collapse: collapse;
}

.summary-table td {
  border: 1px solid #999;
  padding: 8px;
}

.summary-table td.text-right {
  text-align: right;
}

.summary-table tr:last-child td {
  font-weight: bold;
}

.lc-summary-section {
  margin-top: 30px;
  page-break-inside: avoid;
}

.signature-area {
  margin-top: 50px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  page-break-inside: avoid;
}

.signature-box {
  width: 220px;
  text-align: center;
  font-size: 14px;
}

.signature-line {
  border-top: 1px solid #000;
  margin-top: 40px;
  padding-top: 5px;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.full-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

@media print {
  .d-print-none {
    display: none !important;
  }
  .print-container {
    width: 100%;
    padding: 20px;
    box-shadow: none;
  }
  .signature-area {
    page-break-inside: avoid;
    page-break-after: avoid;
    margin-top: 30px !important;
  }
  .pi-section {
    page-break-inside: avoid;
  }
  .pi-header-bar {
    background-color: #607d8b !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color: white !important;
  }
  .pi-details-container {
    display: flex !important;
    border: 1px solid #ddd !important;
    page-break-inside: avoid;
  }
  .pi-details-column {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
  }
  .items-table th,
  .items-table td,
  .summary-table td {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>

